import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { colors } from '../../../../styles/theme';
import useYoutubeOrientation, { YT_ORIENTATION } from '../../../hooks/useYoutubeOrientation';
import {
  getYoutubeEmbedUrl,
  normalizeListingVideos,
} from '../../../utils/listingVideoUtils';

/** Misma capa que ListingImageLightbox: sobre mapa/header; bajo product tour. */
const LIGHTBOX_Z_INDEX = 4000;

const navButtonSx = (side) => ({
  position: 'absolute',
  top: '50%',
  [side]: 12,
  transform: 'translateY(-50%)',
  zIndex: 1,
  color: colors.textPrimary,
  bgcolor: 'rgba(255,255,255,0.08)',
  width: 40,
  height: 40,
  '&:hover': { color: colors.textPrimary, bgcolor: 'rgba(255,255,255,0.15)' },
});

/**
 * Video YouTube ampliado a pantalla casi completa.
 * Solo monta el iframe acá (la grilla usa thumbnail + overlay).
 * Cerrar: X / backdrop / Esc. Con varios videos, flechas / teclado.
 */
export default function ListingVideoLightbox({
  videos = [],
  index = 0,
  onClose,
  onIndexChange,
}) {
  const list = normalizeListingVideos(videos);
  const current = list[index] || null;
  // Hook antes de cualquier return temprano.
  const vertical = useYoutubeOrientation(current) === YT_ORIENTATION.VERTICAL;
  const embedUrl = current
    ? getYoutubeEmbedUrl(current.youtube_video_id, { autoplay: '1' })
    : null;
  const canCycle = list.length > 1;

  const go = (delta) => {
    if (!canCycle) return;
    onIndexChange?.((index + delta + list.length) % list.length);
  };

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
        return;
      }
      if (!canCycle) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        onIndexChange?.((index - 1 + list.length) % list.length);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        onIndexChange?.((index + 1) % list.length);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canCycle, index, list.length, onClose, onIndexChange]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  if (!current || !embedUrl || typeof document === 'undefined') return null;

  return createPortal(
    <Box
      onClick={() => onClose?.()}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: LIGHTBOX_Z_INDEX,
        bgcolor: 'rgba(0,0,0,0.88)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'zoom-out',
        backdropFilter: 'blur(4px)',
      }}
    >
      <IconButton
        aria-label="Cerrar video"
        onClick={(event) => {
          event.stopPropagation();
          onClose?.();
        }}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1,
          color: 'rgba(255,255,255,0.7)',
          '&:hover': { color: colors.textPrimary, bgcolor: 'transparent' },
        }}
      >
        <CloseIcon />
      </IconButton>

      {canCycle ? (
        <IconButton
          aria-label="Video anterior"
          onClick={(event) => {
            event.stopPropagation();
            go(-1);
          }}
          sx={navButtonSx('left')}
        >
          <ChevronLeftIcon />
        </IconButton>
      ) : null}

      <Box
        data-testid="listing-video-lightbox-frame"
        data-orientation={vertical ? YT_ORIENTATION.VERTICAL : YT_ORIENTATION.HORIZONTAL}
        onClick={(event) => event.stopPropagation()}
        sx={{
          position: 'relative',
          width: vertical
            ? 'min(calc(100vw - 112px), calc((100vh - 72px) * 9 / 16), 480px)'
            : 'min(calc(100vw - 112px), calc((100vh - 72px) * 16 / 9), 1280px)',
          aspectRatio: vertical ? '9 / 16' : '16 / 9',
          bgcolor: '#000',
          borderRadius: 1.5,
          overflow: 'hidden',
          cursor: 'default',
          boxShadow: '0 12px 48px rgba(0,0,0,0.45)',
        }}
      >
        <Box
          key={current.youtube_video_id}
          component="iframe"
          src={embedUrl}
          title={`Video de YouTube ${current.youtube_video_id}`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            border: 0,
            display: 'block',
          }}
        />
        {canCycle ? (
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              bottom: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'rgba(255,255,255,0.7)',
              fontSize: '11px',
              whiteSpace: 'nowrap',
              userSelect: 'none',
              px: 1,
              py: 0.25,
              borderRadius: '4px',
              bgcolor: 'rgba(0,0,0,0.45)',
              pointerEvents: 'none',
            }}
          >
            {index + 1} / {list.length}
          </Typography>
        ) : null}
      </Box>

      {canCycle ? (
        <IconButton
          aria-label="Video siguiente"
          onClick={(event) => {
            event.stopPropagation();
            go(1);
          }}
          sx={navButtonSx('right')}
        >
          <ChevronRightIcon />
        </IconButton>
      ) : null}
    </Box>,
    document.body,
  );
}
